import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { stringToBoolean } from "@utils/attributes";
import { attemptMethod } from "@utils/general";

import { useThunkDispatch } from "@redux/dispatcher";
import { addToCart } from "@redux/modules/api/data/cart/cart.module";
import { getPreferredStore } from "@redux/modules/api/data/preferredStore/preferredStore.selectors";
import { update } from "@redux/modules/value/addToCart/addToCart.module";
import { getAddToCartState } from "@redux/modules/value/addToCart/addToCart.selectors";
import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";
import { getProductDetail } from "@redux/modules/value/productDetail/productDetail.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { HuboModal } from "@components/General/HuboModal/huboModal.component";
import { useAccessories } from "@components/Product/Hooks/accessories.hook";

import AddToCartModalComponent from "./addToCartModal.component";
import { AddToCartModalData } from "./types/addToCartModalData.types";

type AddToCartModalContainerProps = {
    json: string;
    custom?: string;
};

const AddToCartModalContainer = ({
    json,
    custom,
}: AddToCartModalContainerProps) => {
    const data = attemptMethod<AddToCartModalData | undefined>(
        () => JSON.parse(json),
        undefined,
    );

    const dispatch = useThunkDispatch();
    const { t } = useGranite();
    const { accessories, loadAccessories } = useAccessories();
    const { deliveryType } = useSelector(getProductDetail);
    const preferredStore = useSelector(getPreferredStore);

    const isCustom = stringToBoolean(custom || "");
    const isOpen = useSelector(getAddToCartState)?.isOpen;

    const onCloseCustom = () => {
        dispatch(update({ isOpen: false }));
    };

    useEffect(() => {
        if (data) {
            loadAccessories(data.sku);
        }
    }, []);

    if (!data) {
        return null;
    }

    const onAddAccessory = (sku: string, product: ProductTileModel) => {
        if (deliveryType) {
            return dispatch(
                addToCart(
                    {
                        deliveryType,
                        quantity: 1,
                        shopId: preferredStore?.code,
                        sku: sku,
                    },
                    product,
                ),
            )
                .then(() => {
                    broadcastMessage({
                        id: "add-to-cart-modal-messages",
                        text: t("product.addtocart.modal.success.addAccessory"),
                        status: "success",
                        dismissible: true,
                    });
                })
                .catch(() => {
                    broadcastMessage({
                        id: "add-to-cart-modal-messages",
                        text: t("product.addtocart.modal.failure.addAccessory"),
                        status: "danger",
                        dismissible: true,
                    });
                });
        }

        return Promise.resolve();
    };

    if (isCustom) {
        return (
            <HuboModal
                title={t("product.addtocart.modal.title")}
                customTitle={title => {
                    return (
                        <h2
                            className="text-secondary"
                            style={{ wordBreak: "break-word" }}
                        >
                            <i
                                className="las la-cart-arrow-down"
                                aria-hidden="true"
                            ></i>
                            {title}
                        </h2>
                    );
                }}
                modalState={!!isOpen}
                toggleHandler={onCloseCustom}
                withFooter={false}
            >
                <AddToCartModalComponent
                    medias={data.medias || []}
                    title={data.title}
                    showHR={true}
                    currentPrice={data.currentPrice}
                    strikethroughPrice={data.strikethroughPrice}
                    promotions={data.promotions ?? []}
                    salesIncentives={data.salesIncentives ?? []}
                    isBoxArticle={data.boxArticle}
                    accessories={accessories || []}
                    onClose={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCloseCustom();
                    }}
                    onAddAccessory={onAddAccessory}
                />
            </HuboModal>
        );
    }

    return (
        <AddToCartModalComponent
            title={data.title}
            medias={data.images?.[0]?.medias || data?.medias || []}
            showHR={!data.boxArticle}
            strikethroughPrice={data.strikethroughPrice}
            currentPrice={data.currentPrice}
            promotions={data.promotions ?? []}
            salesIncentives={data.salesIncentives ?? []}
            isBoxArticle={data.boxArticle}
            accessories={accessories || []}
            onAddAccessory={onAddAccessory}
        />
    );
};

export { AddToCartModalContainer, AddToCartModalContainerProps };
